import { bookServiceApi } from '@/services/api';

// 1. TypeScript Interfaces

export interface SearchBooksParams {
  title?: string;
  author?: string;
  topic?: string;
  price?: string;
  min_price?: number;
  max_price?: number;
  page?: number;
  limit?: number;
  sort_by?: 'rating' | 'newest' | 'curated';
}

export interface Book {
  book_id: number;
  title: string;
  author: string;
  cover_image_url: string | null;
  description: string;
  price: string; // Price is a string in the response
  topic: string;
  content_type?: 'novel' | 'non_fiction' | string;
  created_at: string;
  // Optional average rating for the book (0-5)
  average_rating?: number;
  rating?: number;
  total_ratings?: number;
  time?: string;
  score?: string | number;
  // Whether the book is saved by the current user
  is_saved?: boolean;
  // Whether the book is purchased by the current user
  is_purchased?: boolean;
  // Number of chapters in the book
  chapter_count?: number;
  // Tags associated with the book
  tags?: { tag_id: number; tag_name: string }[];
}

export interface Pagination {
  page: number;
  limit: number;
  total_count: number;
  total_pages: number;
  has_next: boolean;
  has_prev: boolean;
}

export interface SearchBooksResponse {
  books: Book[];
  pagination?: Pagination;
}

// Helper to prepare params for API client
const prepareParams = (params: Record<string, any>): Record<string, string> => {
  const cleanParams: Record<string, string> = {};
  for (const key in params) {
    if (params[key] !== undefined && params[key] !== null) {
      cleanParams[key] = String(params[key]);
    }
  }
  return cleanParams;
};

// 2. API Functions

export const searchBooks = async (
  params: SearchBooksParams
): Promise<SearchBooksResponse> => {
  try {
    const preparedParams = prepareParams(params);
    const response = await bookServiceApi.get<SearchBooksResponse>('/books/search', preparedParams);

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error(response.error || 'Failed to fetch books');
  } catch (error) {
    console.error('Error searching books:', error);
    throw new Error('Failed to fetch books. Please try again later.');
  }
};

/**
 * Get all books or books by category with sorting
 * @param category - Optional category to filter by
 * @param page - Page number (default: 1)
 * @param limit - Number of books per page (default: 50)
 * @param sortBy - Sort field (default: undefined)
 * @param sortOrder - Sort order (default: undefined)
 */
export const getAllBooks = async (
  category?: string,
  page: number = 1,
  limit: number = 50,
  sortBy?: 'rating' | 'newest'
): Promise<SearchBooksResponse> => {
  const params: SearchBooksParams = {
    page,
    limit,
  };

  // If category is provided and not 'all', filter by topic
  if (category && category !== 'all') {
    params.topic = category;
  }

  // Add sorting parameters if provided
  if (sortBy) {
    params.sort_by = sortBy;
  }

  return searchBooks(params);
};

// 3. Get Book Detail Function

export const getBookDetail = async (bookId: number): Promise<Book> => {
  if (!bookId) {
    throw new Error('A book ID must be provided.');
  }

  try {
    const response = await bookServiceApi.get<Book>(`/books/${bookId}`);

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error(response.error || 'Failed to fetch book details');
  } catch (error) {
    console.error(`Error fetching book with ID ${bookId}:`, error);
    throw new Error('Failed to fetch book details. Please try again later.');
  }
};

// 3. Create Book Interfaces & Functions

export interface CreateBookPayload {
  title: string;
  author: string;
  description?: string;
  price?: string;
  topic?: string;
  cover_image?: File;
}

export interface CreatedBook {
  book_id: number;
  title: string;
  author: string;
  cover_image_url: string;
  description: string;
  price: string;
  topic: string;
  created_at: string;
}

/**
 * Creates a new book with optional cover image upload.
 * @param payload - The data for the new book.
 * @returns The newly created book.
 */
export const createBook = async (payload: CreateBookPayload): Promise<CreatedBook> => {
  if (!payload.title || !payload.author) {
    throw new Error('Title and author are required.');
  }

  const formData = new FormData();
  formData.append('title', payload.title);
  formData.append('author', payload.author);
  if (payload.description) {
    formData.append('description', payload.description);
  }
  if (payload.price) {
    formData.append('price', payload.price);
  }
  if (payload.topic) {
    formData.append('topic', payload.topic);
  }
  if (payload.cover_image) {
    formData.append('cover_image', payload.cover_image);
  }

  try {
    const response = await bookServiceApi.post<CreatedBook>('/books/', formData);
    return response.data;
  } catch (error) {
    console.error('Error creating book:', error);
    throw new Error('Failed to create book. Please try again.');
  }
};

// 4. Recommended books for current user (authenticated)
export interface RecommendedBooksParams {
  page?: number;
  limit?: number;
}

export const getRecommendedBooksForUser = async (params: RecommendedBooksParams = {}): Promise<SearchBooksResponse> => {
  try {
    const { page = 1, limit = 10 } = params;
    const preparedParams = prepareParams({ page, limit });
    const response = await bookServiceApi.get<SearchBooksResponse>('/books/user', preparedParams);
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error || 'Failed to fetch recommended books.');
  } catch (error) {
    console.error('Error fetching recommended books for user:', error);
    throw new Error('Failed to fetch recommended books.');
  }
};

// 5. Purchase Book with Gems
export interface PurchaseBookResponse {
  message: string;
  book_id: number;
  gems_spent: number;
  remaining_gems: number;
  is_purchased: boolean;
}

export const purchaseBook = async (bookId: number): Promise<PurchaseBookResponse> => {
  if (!bookId) {
    throw new Error('A book ID must be provided.');
  }

  try {
    const response = await bookServiceApi.post<PurchaseBookResponse>('/library/purchase-book', { book_id: bookId });

    if (response.success && response.data) {
      return response.data;
    }

    // Handle error with detail message
    throw new Error(response.detail || response.error || 'خطا در خرید کتاب');
  } catch (error: any) {
    console.error(`Error purchasing book with ID ${bookId}:`, error);
    throw new Error(error?.message || 'خطا در خرید کتاب');
  }
};
